import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'class-names';
import moment from 'moment';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { ChevronLeft, Loader } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as AUTH_SELECTORS from '../../../store/selectors/auth';

import * as AUTH_ACTIONS from '../../../store/actions/auth';

import TextInput from '../../ui/TextInput/TextInput';

import { emptyFunc } from '../../../helpers/function/emptyFunc';

import { RESET_PASSWORD_STEP } from '../../../const/auth/RESET_PASSWORD_STEP';

import styles from './ResetPasswordForm.module.scss';

const getTimerString = ({ countdown }) => {
    const minutes = moment
        .duration(countdown)
        .minutes()
        .toString()
        .padStart(2, '0');
    const seconds = moment
        .duration(countdown)
        .seconds()
        .toString()
        .padStart(2, '0');
    return `${minutes}:${seconds}`;
};

const Footer = () => {
    const { t } = useTranslation('common');

    const dispatch = useDispatch();

    const {
        step,
        timer,
        isLoading,
    } = useSelector(AUTH_SELECTORS.authSelector);

    const [hasTimeElapsed, setHasTimeElapsed] = useState(false);

    const handleResendCode = useCallback(() => {
        dispatch(AUTH_ACTIONS.requestPasswordReset());
    }, [dispatch]);

    useEffect(() => {
        if (!timer) {
            return emptyFunc;
        }
        setHasTimeElapsed(false);
        const delay = moment(timer).diff(moment(), 'milliseconds');
        const timeout = setTimeout(() => {
            setHasTimeElapsed(true);
        }, delay);
        return () => clearTimeout(timeout);
    }, [timer]);

    return (
        <div className="d-flex flex-column justify-content-center align-items-center w-100">
            <Button
                form="reset-password-form"
                type="submit"
                variant="tint"
                size="lg"
                className={styles.button}
                disabled={isLoading || ([RESET_PASSWORD_STEP.VERIFY_CODE].includes(step) && hasTimeElapsed)}
                data-testid="reset-password-form-submit"
            >
                {isLoading ? (
                    <Loader
                        className="d-block text-center"
                        type="TailSpin"
                        color="#fff"
                        height={28}
                        width={28}
                    />
                ) : (
                    t(`signInScreen.continue`)
                )}
            </Button>

            {[RESET_PASSWORD_STEP.VERIFY_CODE].includes(step) && (
                <Button
                    type="button"
                    variant="light"
                    size="lg"
                    className={classNames(styles.button, styles.secondary)}
                    onClick={handleResendCode}
                    disabled={isLoading || !hasTimeElapsed}
                >
                    {t('signInScreen.resendEmail')}
                </Button>
            )}
        </div>
    );
};

const ResetPasswordForm = (props) => {
    const { showFooter } = props;

    const { t } = useTranslation('common');

    const dispatch = useDispatch();

    const {
        step,
        timer,
        resetPasswordErrors,
    } = useSelector(AUTH_SELECTORS.authSelector);

    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [retypePassword, setRetypePassword] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [countdown, setCountdown] = useState(0);

    const codeRef = useRef(null);
    const passwordRef = useRef(null);
    const retypePasswordRef = useRef(null);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        switch (step) {
            case RESET_PASSWORD_STEP.VERIFY_CODE: {
                dispatch(AUTH_ACTIONS.verifyResetPasswordCode({ code }));
                return;
            }
            case RESET_PASSWORD_STEP.SET_PASSWORD: {
                if (password !== retypePassword) {
                    return setValidationErrors((p) => ({
                        ...p,
                        retypePassword: t('myProfile.editPassword.passwordsDoNotMatch'),
                    }))
                }
                dispatch(AUTH_ACTIONS.setResetPassword({
                    password,
                    code,
                }));
                return;
            }
            default: {
                return;
            }
        }
    }, [
        step,
        dispatch,
        code,
        password,
        retypePassword,
        t,
    ]);

    const handleCodeChange = useCallback((value) => {
        setValidationErrors((p) => ({
            ...p,
            code: null,
        }));
        setCode(value);
    }, []);

    const handlePasswordChange = useCallback((value) => {
        setValidationErrors((p) => ({
            ...p,
            password: null,
        }));
        setPassword(value);
    }, []);

    const handleRetypePasswordChange = useCallback((value) => {
        setValidationErrors((p) => ({
            ...p,
            retypePassword: null,
        }));
        setRetypePassword(value);
    }, []);

    const handleScrollToRef = useCallback(({ ref }) => {
        window.scrollTo(0, ref.current.offsetTop);
    }, []);

    const handleScrollToCode = useCallback(() => {
        handleScrollToRef({ ref: codeRef });
    }, [handleScrollToRef]);

    const handleScrollToPassword = useCallback(() => {
        handleScrollToRef({ ref: passwordRef });
    }, [handleScrollToRef]);

    const handleScrollToRetypePassword = useCallback(() => {
        handleScrollToRef({ ref: passwordRef });
    }, [handleScrollToRef]);

    const handleGoBack = useCallback(() => {
        dispatch(AUTH_ACTIONS.resetStep());
    }, [dispatch]);

    useEffect(() => {
        if (!timer) {
            return emptyFunc;
        }
        setCountdown(moment(timer).diff(moment()).valueOf());
        const interval = setInterval(
            () => {
                setCountdown(moment(timer).diff(moment()).valueOf());
            },
            1000,
        );
        return () => clearInterval(interval);
    }, [timer]);

    useEffect(() => {
        if ([RESET_PASSWORD_STEP.VERIFY_CODE].includes(step)) {
            codeRef.current?.focus?.();
        }
        if ([RESET_PASSWORD_STEP.SET_PASSWORD].includes(step)) {
            passwordRef.current?.focus?.();
        }
    }, [step]);

    return (
        <form
            id="reset-password-form"
            onSubmit={handleSubmit}
            className={styles.container}
        >
            {step && (
                <div className="w-100 mb-4">
                    <button
                        type="button"
                        name="back"
                        className={classNames(
                            styles.backButton,
                            'bg-transparent border-0 px-0 ml-n2',
                        )}
                        onClick={handleGoBack}
                    >
                        <ChevronLeft size={24} />
                        <span
                            className="small text-uppercase font-weight-bold"
                            style={{ position: 'relative', top: 1 }}
                        >
                            {t('signInScreen.back')}
                        </span>
                    </button>
                </div>
            )}
            {[RESET_PASSWORD_STEP.VERIFY_CODE].includes(step) && (
                <>
                    <div className="d-flex flex-column align-items-center w-100 mb-2">
                        <Alert variant="warning">
                            <span>
                                {t('signInScreen.pleaseWaitForEmail')}
                            </span>
                        </Alert>
                        {countdown > 0 && (
                            <div
                                className="d-flex justify-content-center align-items-center rounded-circle border-tint"
                                style={{ width: 64, height: 64 }}
                            >
                                <span className="font-weight-bold">
                                    {getTimerString({ countdown })}
                                </span>
                            </div>
                        )}
                    </div>
                    <TextInput
                        ref={codeRef}
                        type="number"
                        name={t('signInScreen.code')}
                        value={code}
                        setInputValue={handleCodeChange}
                        validationError={(
                            resetPasswordErrors.code ||
                            validationErrors.code
                        )}
                        className={styles.code}
                        onFocus={handleScrollToCode}
                    />
                </>
            )}
            {[RESET_PASSWORD_STEP.SET_PASSWORD].includes(step) && (
                <>
                    <TextInput
                        ref={passwordRef}
                        type='password'
                        name={t('myProfile.editPassword.newPassword')}
                        value={password}
                        validationError={validationErrors.password}
                        setInputValue={handlePasswordChange}
                        onFocus={handleScrollToPassword}
                    />
                    <TextInput
                        ref={retypePasswordRef}
                        type='password'
                        name={t('myProfile.editPassword.retypeNewPassword')}
                        value={retypePassword}
                        validationError={validationErrors.retypePassword}
                        setInputValue={handleRetypePasswordChange}
                        onFocus={handleScrollToRetypePassword}
                    />
                </>
            )}
            {showFooter && (
                <div className="mt-3 w-100">
                    <Footer />
                </div>
            )}
        </form>
    );
};

ResetPasswordForm.Footer = Footer;

ResetPasswordForm.propTypes = {
    showFooter: PropTypes.bool,
};

ResetPasswordForm.defaultProps = {
    showFooter: true,
};

export default ResetPasswordForm;
